import clsx from 'clsx';
import { camelCase, isEmpty } from 'lodash';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  Banner,
  Breadcrumb,
  Gallery,
  HeroBanner,
  Icon,
  LeisureContext,
  LeisureSummary,
  ProductShowSkeleton,
  SanitizedHtml,
  SocialShare,
  TicketMasterPlan,
  TicketMasterSummary
} from 'components';
import { capitalizeFirstLetter, getSubTypeLogo } from 'helpers';
import { routes } from 'router';

import leisureBanner from 'assets/img/leisure-park.jpeg';
import 'assets/styles/components/products/leisure-show.scss';
import 'assets/styles/components/products/product-show.scss';

const LeisureShowPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { leisure, leisureLoading, errorLeisure } = useContext(LeisureContext);

  const isTicketMaster = leisure?.typeItem === 'TICKETMASTER';
  const galleryImages = isTicketMaster ? [{ url: leisure?.vignette }] : leisure?.prices?.images;
  const jQueryScriptExists = document.getElementById('jquery-script');
  const planScriptExists = document.getElementById('plan-script');
  const visual = leisure?.prices?.images?.find((image) => image?.visual)?.visual;

  const breadcrumbs = useMemo(() => {
    const leisurePage = location.pathname.includes('loisirs');
    return [
      { url: routes[leisurePage ? 'leisureHomePage' : 'sportHomePage'].path, label: t('breadcrumb.home') },
      {
        url: leisurePage
          ? `${routes.results.path}?type=leisures`
          : `${routes.results.path}?type=sport&category=${leisure?.category ?? ''}`,
        label: t(`breadcrumb.${leisurePage ? 'leisures' : 'sport'}`)
      },
      { url: '', label: capitalizeFirstLetter(leisure?.title) }
    ];
  }, [leisure, location.pathname]);

  useEffect(() => {
    addTicketMasterScripts();
  }, [leisure]);

  const addTicketMasterScripts = () => {
    if (!isTicketMaster || jQueryScriptExists || planScriptExists) return;

    const jQueryScript = document.createElement('script');
    jQueryScript.src = 'https://ajax.googleapis.com/ajax/libs/jquery/1.7.2/jquery.min.js';
    jQueryScript.id = 'jquery-script';
    document.body.prepend(jQueryScript);

    jQueryScript.onload = () => {
      const planScript = document.createElement('script');
      planScript.src = `${process.env.PUBLIC_URL}/ticketmaster-plan.min.js`;
      planScript.id = 'plan-script';
      document.body.prepend(planScript);
    };
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  return (
    <div className="product-show relative">
      <div className="hidden lg:block">
        <HeroBanner title={leisure?.title || ''} image={{ frontUrl: leisureBanner }} />
      </div>

      <div className="hidden main-container lg:block lg:mb-4 lg:mt-20">
        <Breadcrumb breadcrumbItems={breadcrumbs} loading={leisureLoading} />
      </div>

      <Gallery
        images={[{ url: leisure?.vignette }]}
        loading={leisureLoading}
        isTicketMaster={isTicketMaster}
        className="lg:hidden print:hidden"
      />

      {leisureLoading && <ProductShowSkeleton />}

      {!leisureLoading && errorLeisure && (
        <div className="main-container">
          <Banner icon={{ name: 'circle-info', style: 'fad' }} type="warning" className="my-12">
            {t('contentUnavailable')}
          </Banner>
        </div>
      )}

      {!leisureLoading && !errorLeisure && !isEmpty(leisure) && (
        <div className="product-container main-container">
          <div className="mb-8 lg:hidden">
            <Breadcrumb breadcrumbItems={breadcrumbs} loading={leisureLoading} />
          </div>

          <div className="flex justify-between items-start mb-8">
            <h2 className="h2 underlined-blue">{leisure.title}</h2>

            <div className="gap-4 hidden lg:flex">
              <SocialShare />

              <button type="button" onClick={handleDownloadPDF}>
                <Icon name="print" iconStyle="fad" classes="w-4 h-4 text-gray-500 lg:w-5 lg:h-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-col-reverse gap-y-8 mb-8 print:flex-col-reverse">
            {getSubTypeLogo(leisure.subType) && (
              <div className="w-14">
                <img
                  src={getSubTypeLogo(leisure.subType)}
                  className="w-full h-full object-cover"
                  alt={t(`alts.${camelCase(leisure.subType)}`)}
                  width="56"
                  height="20"
                />
              </div>
            )}

            <Gallery
              images={galleryImages}
              loading={leisureLoading}
              singleImage
              isTicketMaster={isTicketMaster}
              className="hidden lg:block print:block"
            />
          </div>

          <div className="lg:gap-x-16 lg:flex print:flex print:gap-x-6">
            <div className="w-full">
              <SanitizedHtml content={leisure.present} className="mb-2" filterEmpty={!isTicketMaster} />
              <SanitizedHtml
                content={leisure.complementary}
                className={clsx(isTicketMaster ? 'mb-2' : 'mb-16')}
                filterEmpty={!isTicketMaster}
              />
              <SanitizedHtml content={leisure.additionalInfo} className="mb-16" filterEmpty={!isTicketMaster} />
              {isTicketMaster && jQueryScriptExists && planScriptExists && <TicketMasterPlan />}
              {isTicketMaster && visual && <img src={visual} alt="" width="800" height="600" />}
            </div>

            <div className="booking-container print:max-w-[17.5rem]">
              {isTicketMaster ? <TicketMasterSummary /> : <LeisureSummary />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeisureShowPage;
